import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import client from "@/api/client";
import { toast } from "sonner";
import { Zap, Plus, Edit2, Trash2, ArrowLeft, CheckCircle2, Clock, AlertTriangle, TrendingDown } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";

import { PageHeader, SectionCard, MetricCard, CTAButton } from "@/components/ds";
const STATUS_CONFIG = {
  planned:     { label: "Planned",     class: "bg-slate-100 text-slate-600", icon: Clock },
  in_progress: { label: "In Progress", class: "bg-blue-50 text-blue-700",   icon: Zap },
  completed:   { label: "Completed",   class: "bg-emerald-50 text-emerald-700", icon: CheckCircle2 },
  cancelled:   { label: "Cancelled",   class: "bg-red-50 text-red-500",    icon: AlertTriangle },
};

const CAT_LABELS = {
  renewable_energy:       { label: "Renewable Energy",        emoji: "☀️" },
  energy_efficiency:      { label: "Energy Efficiency",       emoji: "⚡" },
  fleet_electrification:  { label: "Fleet Electrification",   emoji: "🚗" },
  waste_reduction:        { label: "Waste Reduction",         emoji: "♻️" },
  process_optimisation:   { label: "Process Optimisation",    emoji: "🔧" },
  materials_substitution: { label: "Materials Substitution",  emoji: "📦" },
  other:                  { label: "Other",                   emoji: "💡" },
};

const CONFIDENCE_CONFIG = {
  high:   { label: "High confidence",   class: "bg-emerald-50 text-emerald-700 border border-emerald-200" },
  medium: { label: "Medium confidence", class: "bg-blue-50 text-blue-700 border border-blue-200" },
  low:    { label: "Low confidence",    class: "bg-amber-50 text-amber-700 border border-amber-200" },
};

const STATUSES = ["planned", "in_progress", "completed", "cancelled"];
const CATEGORIES = Object.keys(CAT_LABELS);

export default function CorporateInitiatives() {
  const navigate = useNavigate();
  const [initiatives, setInitiatives] = useState([]);
  const [assets, setAssets] = useState([]);
  const [bus, setBus] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState({
    name: "", description: "", category: "renewable_energy", status: "planned",
    confidence_level: "medium",
    asset_id: "", bu_id: "", expected_reduction_tco2e: "", actual_reduction_tco2e: "",
    target_completion_year: new Date().getFullYear() + 1, capex_usd: "", notes: ""
  });
  const [saving, setSaving] = useState(false);
  const { currentOrg } = useAuth();
  const orgId = currentOrg?.id || 1;

  const load = async () => {
    setLoading(true);
    try {
      const [iRes, aRes, bRes] = await Promise.all([
        client.get(`/corporate/orgs/${orgId}/initiatives`),
        client.get(`/assets?org_id=${orgId}`),
        client.get(`/corporate/orgs/${orgId}/business-units`),
      ]);
      setInitiatives(iRes.data);
      setAssets(aRes.data);
      setBus(bRes.data);
    } catch {}
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        asset_id: form.asset_id ? parseInt(form.asset_id) : null,
        bu_id: form.bu_id ? parseInt(form.bu_id) : null,
        expected_reduction_tco2e: form.expected_reduction_tco2e ? parseFloat(form.expected_reduction_tco2e) : null,
        actual_reduction_tco2e: form.actual_reduction_tco2e ? parseFloat(form.actual_reduction_tco2e) : null,
        target_completion_year: parseInt(form.target_completion_year),
        capex_usd: form.capex_usd ? parseFloat(form.capex_usd) : null,
        confidence_level: form.confidence_level || "medium",
      };
      if (editItem) {
        await client.put(`/corporate/initiatives/${editItem.id}`, payload);
        toast.success("Initiative updated");
      } else {
        await client.post(`/corporate/orgs/${orgId}/initiatives`, payload);
        toast.success("Initiative created");
      }
      setShowCreate(false);
      setEditItem(null);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    setSaving(false);
  };

  const del = async (id) => {
    if (!window.confirm("Delete this initiative?")) return;
    try { await client.delete(`/corporate/initiatives/${id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Failed"); }
  };

  const openEdit = (i) => {
    setEditItem(i);
    setForm({
      name: i.name, description: i.description || "", category: i.category, status: i.status,
      confidence_level: i.confidence_level || "medium",
      asset_id: i.asset_id || "", bu_id: i.bu_id || "",
      expected_reduction_tco2e: i.expected_reduction_tco2e || "",
      actual_reduction_tco2e: i.actual_reduction_tco2e || "",
      target_completion_year: i.target_completion_year || new Date().getFullYear() + 1,
      capex_usd: i.capex_usd || "", notes: i.notes || ""
    });
    setShowCreate(true);
  };

  // Summary stats
  const totalExpected = initiatives.reduce((s, i) => s + (i.expected_reduction_tco2e || 0), 0);
  const totalAchieved = initiatives.reduce((s, i) => s + (i.actual_reduction_tco2e || 0), 0);
  const grouped = { planned: [], in_progress: [], completed: [], cancelled: [] };
  initiatives.forEach(i => { if (grouped[i.status]) grouped[i.status].push(i); });

  return (
    <div className="p-6 lg:p-8 space-y-6 animate-fade-in">
      <div className="flex items-start justify-between">
          
      <PageHeader
        title="Decarbonisation Initiatives"
        subtitle="Track emission reduction projects — planned vs actual impact."
        backUrl="/corporate"
        backLabel="Corporate Dashboard"
      />
        <button onClick={() => { setEditItem(null); setForm({ name: "", description: "", category: "renewable_energy", status: "planned", asset_id: "", bu_id: "", expected_reduction_tco2e: "", actual_reduction_tco2e: "", target_completion_year: new Date().getFullYear() + 1, capex_usd: "", notes: "" }); setShowCreate(true); }}
          className="inline-flex items-center gap-2 px-4 py-2 bg-[#0F172A] text-white text-sm font-medium rounded-lg hover:bg-[#1E293B]" data-testid="create-initiative-btn">
          <Plus size={14} /> New Initiative
        </button>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <MetricCard label="Total Initiatives"  value={initiatives.length}                                                           color="text-slate-900" />
        <MetricCard label="Planned Reduction"  value={`${totalExpected.toFixed(1)} t/yr`}                                           color="text-blue-600" />
        <MetricCard label="Achieved"           value={`${totalAchieved.toFixed(1)} t/yr`}                                           color="text-[#059669]" />
        <MetricCard label="Achievement"        value={totalExpected > 0 ? `${((totalAchieved / totalExpected) * 100).toFixed(0)}%` : "—"} color="text-amber-600" />
      </div>

      {/* By Status */}
      {STATUSES.filter(s => grouped[s]?.length > 0).map(status => {
        const sc = STATUS_CONFIG[status];
        const Icon = sc.icon;
        return (
          <div key={status}>
            <div className="flex items-center gap-2 mb-3">
              <Icon size={14} className="text-slate-500" />
              <h3 className="text-sm font-semibold text-slate-900">{sc.label} ({grouped[status].length})</h3>
            </div>
            <div className="space-y-3">
              {grouped[status].map(i => {
                const cat = CAT_LABELS[i.category] || CAT_LABELS.other;
                const pct = i.achievement_pct;
                return (
                  <div key={i.id} className="bg-white border border-slate-200 rounded-xl p-5" data-testid={`initiative-${i.id}`}>
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-start gap-3">
                        <span className="text-xl">{cat.emoji}</span>
                        <div>
                          <p className="text-sm font-semibold text-slate-900">{i.name}</p>
                          <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${sc.class}`}>{sc.label}</span>
                            <span className="text-xs text-slate-400">{cat.label}</span>
                            {i.asset_name && <span className="text-xs text-slate-400">· {i.asset_name}</span>}
                            {i.target_completion_year && <span className="text-xs text-slate-400">· Due {i.target_completion_year}</span>}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button onClick={() => openEdit(i)} className="text-slate-300 hover:text-slate-600"><Edit2 size={13} /></button>
                        <button onClick={() => del(i.id)} className="text-slate-300 hover:text-red-500"><Trash2 size={13} /></button>
                      </div>
                    </div>
                    {i.description && <p className="text-xs text-slate-500 mb-3 leading-relaxed">{i.description}</p>}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-xs">
                      <div>
                        <p className="text-slate-400 mb-0.5">Expected Reduction</p>
                        <p className="font-bold text-blue-600">{i.expected_reduction_tco2e?.toFixed(2) || "—"} tCO₂e/yr</p>
                      </div>
                      <div>
                        <p className="text-slate-400 mb-0.5">Achieved</p>
                        <p className="font-bold text-[#059669]">{i.actual_reduction_tco2e?.toFixed(2) || "0"} tCO₂e/yr</p>
                      </div>
                      <div>
                        <p className="text-slate-400 mb-0.5">Confidence</p>
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${CONFIDENCE_CONFIG[i.confidence_level || "medium"]?.class || ""}`}>
                          {CONFIDENCE_CONFIG[i.confidence_level || "medium"]?.label}
                        </span>
                      </div>
                      {i.gap_contribution_pct != null ? (
                        <div>
                          <p className="text-slate-400 mb-0.5">Gap Closure</p>
                          <p className={`font-bold ${i.gap_contribution_pct >= 30 ? "text-[#059669]" : i.gap_contribution_pct >= 10 ? "text-blue-600" : "text-slate-500"}`}>
                            {i.gap_contribution_pct.toFixed(0)}% of target gap
                          </p>
                        </div>
                      ) : i.capex_usd ? (
                        <div>
                          <p className="text-slate-400 mb-0.5">CapEx</p>
                          <p className="font-semibold text-slate-900">${i.capex_usd.toLocaleString()}</p>
                        </div>
                      ) : null}
                    </div>
                    {i.expected_reduction_tco2e > 0 && (
                      <div className="mt-3">
                        <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                          <div className="h-full bg-[#059669] rounded-full transition-all"
                               style={{ width: `${Math.min(100, pct || 0)}%` }} />
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}

      {initiatives.length === 0 && (
        <SectionCard>
          <div className="py-10 text-center">
            <Zap size={32} className="mx-auto mb-3 text-slate-200" />
            <p className="text-sm text-slate-500">No initiatives yet</p>
            <p className="text-xs text-slate-400 mt-1">Add decarbonisation initiatives to track your emission reduction progress.</p>
          </div>
        </SectionCard>
      )}

      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{editItem ? "Edit Initiative" : "New Initiative"}</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-4 mt-2 max-h-[70vh] overflow-y-auto pr-1">
            <div>
              <label className="block text-xs font-medium uppercase tracking-wider text-slate-500 mb-1.5">Name *</label>
              <input type="text" value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))} required
                className="w-full h-10 px-3 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#059669]"
                placeholder="e.g. Rooftop Solar Installation" data-testid="initiative-name-input" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium uppercase tracking-wider text-slate-500 mb-1.5">Category</label>
                <select value={form.category} onChange={e => setForm(f => ({...f, category: e.target.value}))}
                  className="w-full h-10 px-3 border border-slate-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#059669]">
                  {CATEGORIES.map(c => <option key={c} value={c}>{CAT_LABELS[c].label}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium uppercase tracking-wider text-slate-500 mb-1.5">Status</label>
                <select value={form.status} onChange={e => setForm(f => ({...f, status: e.target.value}))}
                  className="w-full h-10 px-3 border border-slate-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#059669]">
                  {STATUSES.map(s => <option key={s} value={s}>{STATUS_CONFIG[s].label}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium uppercase tracking-wider text-slate-500 mb-1.5">Confidence Level</label>
              <div className="flex gap-2">
                {["high", "medium", "low"].map(cl => (
                  <button key={cl} type="button" onClick={() => setForm(f => ({...f, confidence_level: cl}))}
                    className={`flex-1 h-9 text-xs font-medium rounded-lg border transition-colors ${form.confidence_level === cl ? CONFIDENCE_CONFIG[cl]?.class : "border-slate-200 text-slate-500 hover:bg-slate-50"}`}>
                    {CONFIDENCE_CONFIG[cl]?.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium uppercase tracking-wider text-slate-500 mb-1.5">Expected Reduction (tCO₂e/yr)</label>
                <input type="number" step="0.01" value={form.expected_reduction_tco2e} onChange={e => setForm(f => ({...f, expected_reduction_tco2e: e.target.value}))}
                  className="w-full h-10 px-3 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#059669]" placeholder="0.00" />
              </div>
              <div>
                <label className="block text-xs font-medium uppercase tracking-wider text-slate-500 mb-1.5">Actual Achieved (tCO₂e/yr)</label>
                <input type="number" step="0.01" value={form.actual_reduction_tco2e} onChange={e => setForm(f => ({...f, actual_reduction_tco2e: e.target.value}))}
                  className="w-full h-10 px-3 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#059669]" placeholder="0.00" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium uppercase tracking-wider text-slate-500 mb-1.5">Asset</label>
                <select value={form.asset_id} onChange={e => setForm(f => ({...f, asset_id: e.target.value}))}
                  className="w-full h-10 px-3 border border-slate-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#059669]">
                  <option value="">All assets</option>
                  {assets.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium uppercase tracking-wider text-slate-500 mb-1.5">CapEx (USD)</label>
                <input type="number" value={form.capex_usd} onChange={e => setForm(f => ({...f, capex_usd: e.target.value}))}
                  className="w-full h-10 px-3 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#059669]" placeholder="Optional" />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium uppercase tracking-wider text-slate-500 mb-1.5">Description</label>
              <textarea value={form.description} onChange={e => setForm(f => ({...f, description: e.target.value}))}
                className="w-full p-3 border border-slate-300 rounded-lg text-sm resize-none h-16 focus:outline-none focus:ring-2 focus:ring-[#059669]" />
            </div>
            <div className="flex gap-3 pt-1">
              <button type="button" onClick={() => { setShowCreate(false); setEditItem(null); }} className="flex-1 h-10 border border-slate-300 text-sm rounded-lg hover:bg-slate-50">Cancel</button>
              <button type="submit" disabled={saving} className="flex-1 h-10 bg-[#059669] text-white text-sm font-medium rounded-lg hover:bg-emerald-700 disabled:opacity-60" data-testid="initiative-submit-btn">
                {saving ? "Saving..." : editItem ? "Update" : "Create Initiative"}
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
